import { expenseShareBasisPoints } from './expense-history.js';

export interface ExpensePaymentPart {
  readonly sourceConnectionId: string;
  readonly categoryId: string;
  readonly paidCents: bigint;
}

/** Répartition des paiements nets, distincte du poids des charges HT dans le CA.
 * Les remboursements gardent leur signe ; leur part peut être négative et une
 * autre part dépasser 100 %. Un total nul/négatif ne devient pas une base
 * positive artificielle. Les identités dupliquées rendent la base ambiguë.
 */
export class ExpensePaymentDistribution {
  public constructor(private readonly parts: readonly ExpensePaymentPart[]) {}
  /** Barre à 100 % : uniquement les montants positifs nets par catégorie. Les
   * remboursements restent hors barre et visibles séparément. Les plus grands
   * restes répartissent les centièmes manquants pour conserver exactement 100 %.
   * Cet ajustement visuel ne change aucun montant monétaire. */
  public positiveShares(): readonly (ExpensePaymentPart & {
    readonly basisPoints: number | null;
  })[] {
    const keys = this.parts.map((part) =>
      JSON.stringify([part.sourceConnectionId, part.categoryId]),
    );
    const total = this.parts.reduce(
      (sum, part) => sum + (part.paidCents > 0n ? part.paidCents : 0n),
      0n,
    );
    if (total <= 0n || new Set(keys).size !== keys.length)
      return this.parts.map((part) => ({ ...part, basisPoints: null }));
    const rows = this.parts.map((part, index) => ({
      ...part,
      index,
      basisPoints: part.paidCents < 0n ? null : Number((part.paidCents * 10000n) / total),
      remainder: part.paidCents > 0n ? (part.paidCents * 10000n) % total : 0n,
    }));
    const missing = 10000 - rows.reduce((sum, row) => sum + (row.basisPoints ?? 0), 0);
    const order = rows
      .filter((row) => row.paidCents > 0n)
      .sort((a, b) =>
        a.remainder === b.remainder
          ? keys[a.index]!.localeCompare(keys[b.index]!)
          : a.remainder > b.remainder
            ? -1
            : 1,
      );
    for (const row of order.slice(0, missing)) row.basisPoints = row.basisPoints! + 1;
    return rows.map(({ sourceConnectionId, categoryId, paidCents, basisPoints }) => ({
      sourceConnectionId,
      categoryId,
      paidCents,
      basisPoints,
    }));
  }
  public shares(): readonly (ExpensePaymentPart & { readonly basisPoints: number | null })[] {
    const keys = this.parts.map((part) =>
      JSON.stringify([part.sourceConnectionId, part.categoryId]),
    );
    const total = this.parts.reduce((sum, part) => sum + part.paidCents, 0n);
    const unique = new Set(keys).size === keys.length;
    return this.parts.map((part) => ({
      ...part,
      basisPoints: unique ? expenseShareBasisPoints(part.paidCents, total) : null,
    }));
  }
}
